import { __ } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( ', ' ) ?? '';

function taxFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.name === filterValue ) ?? false;
}
taxFilterFn.autoRemove = ( val ) => ! val;

/**
 * Helper: flatten a WPGraphQL post-type relationship connection to a comma-separated string.
 * Post type pick fields come back as { nodes: [{ title }] }.
 * @param connection
 */
const postTitles = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.title ).join( ', ' ) ?? '';

/**
 * filterFn for post-type relationship columns: checks whether the selected
 * post title appears in the raw WPGraphQL connection stored on row.original[columnId].
 * @param row
 * @param columnId
 * @param filterValue
 */
function postRefFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.title === filterValue ) ?? false;
}
postRefFilterFn.autoRemove = ( val ) => ! val;

/**
 * Column definitions for the administration CPT.
 *
 * @param {Function} onDelete Called with databaseId when the user confirms deletion.
 */
export function administrationColumns( { onDelete } ) {
	return [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this administration to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( 'title', {
			header: __( 'Administration', 'anacrm' ),
			cell: ( { row, getValue } ) => (
				<a
					href={ row.original.uri }
					className="font-semibold text-blue hover:!no-underline"
				>
					{ getValue() }
				</a>
			),
		} ),

		col.accessor( ( row ) => taxNames( row.territories ), {
			id: 'territories',
			header: __( 'Territory', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.regions ), {
			id: 'regions',
			header: __( 'Region', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.cities ), {
			id: 'cities',
			header: __( 'Municipality', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.geoscopes ), {
			id: 'geoscopes',
			header: __( 'Geographical scope', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => postTitles( row.administrationUpper ), {
			id: 'administrationUpper',
			header: __( 'Mother administration', 'anacrm' ),
			filterFn: postRefFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.workScopes ), {
			id: 'workScopes',
			header: __( 'Work scope', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => postTitles( row.contactPersonRef ), {
			id: 'contactPersonRef',
			header: __( 'Contact person', 'anacrm' ),
		} ),

		col.accessor( 'contactMail', {
			header: __( 'Email', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				return v ? (
					<a
						href={ `mailto:${ v }` }
						className="text-blue hover:!no-underline"
					>
						{ v }
					</a>
				) : (
					'—'
				);
			},
		} ),

		col.accessor( 'contactPhone', {
			header: __( 'Phone', 'anacrm' ),
			cell: ( { getValue } ) => getValue() || '—',
		} ),
	];
}
